import _ from 'lodash'
import { DateTime } from 'luxon'

export const typeOf = (value: any): string => {
	return Object.prototype.toString.call(value).slice(8, -1)
}

// fast implementation for potentially large objects
export const hasEntries = (o: object): boolean => {
	for (const _ in o) return true
	return false
}

export const padZeros = (n: number, digits: number): string => {
	return String(n).padStart(digits, '0')
}

const ID_CHARS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'
const ID_CHARS_LEN = ID_CHARS.length

// this is equivalently fast to crypto.randomUUID() in Safari
// 100k ids < 20ms on m2
export const cid24 = (prefix?: string) => (
	(prefix ?? 'c') +
	ID_CHARS[Math.floor(Math.random() * ID_CHARS_LEN)] +
	ID_CHARS[Math.floor(Math.random() * ID_CHARS_LEN)] +
	ID_CHARS[Math.floor(Math.random() * ID_CHARS_LEN)] +
	ID_CHARS[Math.floor(Math.random() * ID_CHARS_LEN)] +
	ID_CHARS[Math.floor(Math.random() * ID_CHARS_LEN)] +
	ID_CHARS[Math.floor(Math.random() * ID_CHARS_LEN)] +
	ID_CHARS[Math.floor(Math.random() * ID_CHARS_LEN)] +
	ID_CHARS[Math.floor(Math.random() * ID_CHARS_LEN)] +
	ID_CHARS[Math.floor(Math.random() * ID_CHARS_LEN)] +
	ID_CHARS[Math.floor(Math.random() * ID_CHARS_LEN)] +
	ID_CHARS[Math.floor(Math.random() * ID_CHARS_LEN)] +
	ID_CHARS[Math.floor(Math.random() * ID_CHARS_LEN)] +
	ID_CHARS[Math.floor(Math.random() * ID_CHARS_LEN)] +
	ID_CHARS[Math.floor(Math.random() * ID_CHARS_LEN)] +
	ID_CHARS[Math.floor(Math.random() * ID_CHARS_LEN)] +
	ID_CHARS[Math.floor(Math.random() * ID_CHARS_LEN)] +
	ID_CHARS[Math.floor(Math.random() * ID_CHARS_LEN)] +
	ID_CHARS[Math.floor(Math.random() * ID_CHARS_LEN)] +
	ID_CHARS[Math.floor(Math.random() * ID_CHARS_LEN)] +
	ID_CHARS[Math.floor(Math.random() * ID_CHARS_LEN)] +
	ID_CHARS[Math.floor(Math.random() * ID_CHARS_LEN)] +
	ID_CHARS[Math.floor(Math.random() * ID_CHARS_LEN)] +
	ID_CHARS[Math.floor(Math.random() * ID_CHARS_LEN)]
)

export const stringId = (len: number): string => {
	let id = ''
	for(let i = 1; i <= len; i++) {
		id += ID_CHARS[Math.floor(Math.random() * ID_CHARS_LEN)]
	}
	return id
}

// const cidSpeedTest = () => {
// 	const uuid1 = Date.now()
// 	for(let i = 0; i < 100000; i++) {
// 		crypto.randomUUID()
// 	}
// 	console.info(`100k uuid ${Date.now() - uuid1}ms`)
// 	const cid1 = Date.now()
// 	for(let i = 0; i < 100000; i++) {
// 		cid24()
// 	}
// 	console.info(`100k cid ${Date.now() - cid1}ms`)
// }
// cidSpeedTest()

// emotion specifically requires only lowercase letters
const LC_CHARS = 'abcdefghijklmnopqrstuvwxyz'
export const randomLowercaseId = () => (
	LC_CHARS[Math.floor(Math.random() * 26)] +
	LC_CHARS[Math.floor(Math.random() * 26)] +
	LC_CHARS[Math.floor(Math.random() * 26)] +
	LC_CHARS[Math.floor(Math.random() * 26)] +
	LC_CHARS[Math.floor(Math.random() * 26)] +
	LC_CHARS[Math.floor(Math.random() * 26)] +
	LC_CHARS[Math.floor(Math.random() * 26)] +
	LC_CHARS[Math.floor(Math.random() * 26)]
)

export const sqlServerDateTimeOffset = (dt: DateTime) => {
	let s = dt.toFormat(`yyyy-MM-dd'T'HH:mm:ss.SSS0000ZZ`)
	if(s.endsWith('+00:00')) {
		s = s.substring(0, s.length - 6) + 'Z'
	}
	return s
}
export const timestamp = () => sqlServerDateTimeOffset(DateTime.now())
export const utcTimestamp = () => sqlServerDateTimeOffset(DateTime.utc())
export const datestamp = () => DateTime.now().toISODate()
export const logTime = () => DateTime.now().toFormat('HH:mm:ss')
export const ageOn = (birth: DateTime, on: DateTime) => Math.floor(on.diff(birth, 'years').as('years'))
export const age = (birth: DateTime) => ageOn(birth, DateTime.now().startOf('day'))
export const plainDateTime = (dt: DateTime) => dt.toFormat('yyyy-MM-dd h:mm a')
export const plainDateTimeSecs = (dt: DateTime) => dt.toFormat('yyyy-MM-dd h:mm:ss a')
export const dateAndDow = (dt: DateTime) => dt.toFormat('yyyy-MM-dd EEEE')
export const dateAndDowShort = (dt: DateTime) => dt.toFormat('yyyy-MM-dd EEE')

export const splitCommaString = (s: string|Array<string>|null) => {
	if(!_.isString(s)) {
		return s
	}
	const parts = []
	for(let part of s.split(',')) {
		part = part.trim()
		if(part === '') {
			continue
		}
		parts.push(part.trim())
	}
	return parts
}

// ␤ = https://www.compart.com/en/unicode/U+2424
export const encodeLineBreaks = (s: string) => s.replaceAll(/(\r\n|\r|\n)/g, '␤')
export const decodeLineBreaks = (s: string) => s.replaceAll('␤', '\n')

export const onlyDigits = (s: string): string => s.match(/\d+/)?.join('') ?? ''

export const usPhoneDisplay = (s: string): string => {
	const d = onlyDigits(s)
	if(d.length < 10) {
		return s
	}
	return d.substring(0,3) + '-' + d.substring(3,6) + '-' + d.substring(6)
}

export const shortGuid = (guid: string) => {
	const parts = guid.split('-')
	if(parts.length !== 5) {
		// invalid guid
		return guid
	}
	// paula says one part only, we agree, dan and i like the first part
	return parts[0]
}

// a sophisticated pluralisation engine™
export const s = (word: string, value: number) => {
	if(value !== 1) {
		if(word.slice(-1) === 'y') {
			return word.slice(0, -1) + 'ies'
		}
		return word + 's'
	}
	return word
}

// cant believe lodash doesnt have these
export const findIndexFrom = <T>(array: T[], predicate: (item: T, idx: number) => boolean, startIndex: number = 0) => {
	if (startIndex < 0 || startIndex >= array.length) {
		return -1
	}

	for (let i = startIndex; i < array.length; i++) {
		if (predicate(array[i], i)) {
			return i;
		}
	}

	return -1; // not found
}

export const findPreviousIndexFrom = <T>(array: T[], predicate: (item: T, idx: number) => boolean, startIndex: number = 0) => {
	if (startIndex < 0 || startIndex >= array.length) {
		return -1
	}

	for (let i = startIndex; i >= 0; i--) {
		if (predicate(array[i], i)) {
			return i;
		}
	}

	return -1; // not found
}

// zzzzz
export const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))
